import { useState, useEffect, useContext } from 'react';
import { analytics } from '@services/analytics';
import { useFacebookConnection } from './useFacebookConnection';
import { dtos } from '@shd/jslib/models';
import { TeamContext, UserContext } from '@services/Session';

export type FacebookVariant =
  | 'team-page'
  | 'livestream-setup'
  | 'team-settings';

interface UseFacebookLinkingOptions {
  variant: FacebookVariant;
  liveTeamId?: string | null;
}

function isValidDestination(dest: dtos.PublishFb | null): boolean {
  return (
    !!dest &&
    typeof dest.destNodeType === 'string' &&
    dest.destNodeType !== 'profile'
  );
}

export function useFacebookLinking({
  variant,
  liveTeamId = null,
}: UseFacebookLinkingOptions) {
  const teamStore = useContext(TeamContext);
  const userStore = useContext(UserContext);
  const userId = userStore.authUser?.claims?.shd_user_id || '';
  const teamId = liveTeamId ? liveTeamId : teamStore.id;
  const sportType = teamStore.sport;

  const {
    loading,
    error,
    publishFB,
    setPublishFB,
    fbScopes,
    responseFacebook,
    onUnlinkFacebookClick,
    onSave,
    showExpiration,
    showLinkingSuccess,
    isLivestreamEligible,
    showLinkingModal,
    destination,
    linkingLoading,
    onLinkFacebookClick,
    showIntroModal,
    setShowIntroModal,
    fbPublishState,
    setShowLinkingModal,
    onClose,
  } = useFacebookConnection({ liveTeamId });

  const [showHowTo, setShowHowTo] = useState(false);
  const [showLinking, setShowLinking] = useState(false);
  const [stepDestinationTitle, setStepDestinationTitle] = useState(
    'Where do you want to stream to?'
  );
  const [destinationGoBack, setDestinationGoBack] = useState<() => void>();

  useEffect(() => {
    if (publishFB && variant === 'team-page') {
      analytics.track('Stream Destination Reconnected', {
        userId,
        teamId,
        sport: sportType,
        destinationType: 'Facebook',
        destinationName: publishFB.destNodeName,
      });
    }
  }, [publishFB, variant, userId, teamId, sportType]);

  return {
    publishFB,
    error,
    fbScopes,
    responseFacebook,
    loading,
    onLinkFacebookClick,
    onUnlinkFacebookClick,
    setPublishFB,
    isLinked: isValidDestination(publishFB),
    showExpiration,
    linkingLoading,
    fbPublishState,
    ui: {
      showIntroModal,
      setShowIntroModal,
      showHowTo,
      setShowHowTo,
      showLinking,
      setShowLinking,
      stepDestinationTitle,
      setStepDestinationTitle,
      destinationGoBack,
      setDestinationGoBack,
      showLinkingSuccess,
      isLivestreamEligible,
      showLinkingModal,
      destination,
      setShowLinkingModal,
    },
    handlers: {
      onClose,
      setPublishFB,
      responseFacebook,
      onSave,
    },
  };
}
